import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Star, Loader2, Trash2, MessageSquareQuote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null);

    const fetchAll = async () => {
        try {
            const { data } = await axios.get(`${API}/admin/reviews`, { withCredentials: true });
            setReviews(data);
        } catch (e) {
            toast.error('Failed to load reviews');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const remove = async (id) => {
        if (!window.confirm('Delete this review permanently? It will disappear from the landing page.')) return;
        setDeleting(id);
        try {
            await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
            toast.success('Review deleted');
            setReviews((r) => r.filter((x) => x.id !== id));
        } catch (e) {
            toast.error('Failed to delete');
        } finally {
            setDeleting(null);
        }
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-4xl mx-auto px-4 py-10">
                <div className="flex items-center gap-3 mb-2">
                    <MessageSquareQuote className="w-6 h-6" style={{ color: 'var(--primary-color)' }} />
                    <h1 className="text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>Student Reviews</h1>
                </div>
                <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
                    These appear live on the landing page testimonials section. Delete any that are not suitable.
                </p>

                {loading ? (
                    <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--primary-color)' }} /></div>
                ) : reviews.length === 0 ? (
                    <p className="text-center py-16" style={{ color: 'var(--text-muted)' }} data-testid="no-reviews">No reviews yet.</p>
                ) : (
                    <div className="space-y-4" data-testid="admin-reviews-list">
                        {reviews.map((r) => (
                            <div key={r.id} className="card-surface p-5 flex items-start justify-between gap-4" data-testid={`review-row-${r.id}`}>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                                        <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{r.name}</span>
                                        {r.country && (
                                            <span className="px-1.5 py-0.5 rounded text-[10px] font-black" style={{ backgroundColor: 'var(--primary-muted)', color: 'var(--primary-color)', border: '1px solid var(--border-highlight)' }}>{r.country}</span>
                                        )}
                                        <span className="flex items-center gap-0.5">
                                            {[1, 2, 3, 4, 5].map((n) => (
                                                <Star key={n} className="w-3.5 h-3.5" style={{ color: '#FFB800', fill: r.rating >= n ? '#FFB800' : 'transparent' }} />
                                            ))}
                                        </span>
                                        {r.seeded && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(138,143,163,0.15)', color: 'var(--text-muted)' }}>starter</span>}
                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                                    </div>
                                    <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{r.text}</p>
                                </div>
                                <Button size="sm" variant="outline" onClick={() => remove(r.id)} disabled={deleting === r.id} style={{ color: '#EF4444' }} data-testid={`delete-review-${r.id}`}>
                                    {deleting === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                </Button>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
